"use client";

import { c as _c } from "react/compiler-runtime";
import { Moon, Sun } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { VISITOR_THEME_CHOICE_STORAGE_KEY, VISITOR_THEME_PERSIST_ATTR } from "../theme/visitorThemeModes";
import { parseDualColorValue } from "../ui/lib/color-mode-value";
import type { ThemeSwitcherSize, ThemeSwitcherVariant } from "../ui/types";
import styles from "./visitor-theme-switcher-control.module.css";

/**
 * Canvas / Region interaction mode for the control chrome.
 * (Formerly the prop named `variant` — that name now means icon|segmented.)
 */
export type VisitorThemeSwitcherInteraction = "live" | "preview" | "disabled";

/** @deprecated Prefer {@link VisitorThemeSwitcherInteraction}. */
export type VisitorThemeSwitcherVariant = VisitorThemeSwitcherInteraction;
export type VisitorThemeSwitcherControlProps = {
  /**
   * - `"live"` — working toggle (Region when global gate + instance `show`).
   * - `"preview"` — static canvas chrome when the global switcher is on.
   * - `"disabled"` — non-interactive chrome (global off on canvas, or
   *   instance `displayMode="disabled"` on Region/canvas).
   */
  interaction: VisitorThemeSwitcherInteraction;
  /** Icon button vs Light|Dark segmented pill; default `segmented`. */
  variant?: ThemeSwitcherVariant;
  size?: ThemeSwitcherSize;
  iconColor?: string;
  borderColor?: string;
  activeBackground?: string;
  /** Optional static override for the accessible name. */
  ariaLabel?: string;
  /**
   * Builder canvas only: selectable "Hidden" placeholder. Region returns
   * `null` for `displayMode="hide"` instead.
   */
  showHiddenPlaceholder?: boolean;
  /** Which disabled-state title to show when `interaction="disabled"`. */
  disabledReason?: "global" | "instance";
};
const GLOBAL_DISABLED_HINT = "Enable in Theme Design → Default Theme → Allow visitors to switch themes";
const INSTANCE_DISABLED_HINT = "This placement is set to Disabled — visible but non-interactive on the live site";
const PREVIEW_HINT = "Visitors will see a working Light|Dark toggle here";
function applyVtsColorVar(root: HTMLElement, name: "icon" | "border" | "active-bg", value: string | undefined): void {
  if (!value?.trim()) {
    root.style.removeProperty(`--vts-${name}`);
    root.style.removeProperty(`--vts-${name}-dark`);
    return;
  }
  const dual = parseDualColorValue(value);
  if (dual) {
    root.style.setProperty(`--vts-${name}`, dual.light);
    root.style.setProperty(`--vts-${name}-dark`, dual.dark);
    return;
  }
  root.style.setProperty(`--vts-${name}`, value);
  root.style.removeProperty(`--vts-${name}-dark`);
}
function sizeClass(size: ThemeSwitcherSize | undefined): string {
  if (size === "sm") {
    return styles.sizeSm;
  }
  if (size === "lg") {
    return styles.sizeLg;
  }
  return styles.sizeMd;
}
function readIsDarkFromDom(el: HTMLElement | null): boolean {
  if (typeof document === "undefined") {
    return false;
  }
  if (document.documentElement.classList.contains("dark")) {
    return true;
  }
  const canvasHost = el?.closest("[data-builder-canvas-mode]");
  return canvasHost?.getAttribute("data-builder-canvas-mode") === "dark";
}
function persistVisitorChoice(nextDark: boolean): void {
  const root = document.documentElement;
  if (root.getAttribute(VISITOR_THEME_PERSIST_ATTR) !== "1") {
    return;
  }
  try {
    window.localStorage.setItem(VISITOR_THEME_CHOICE_STORAGE_KEY, nextDark ? "dark" : "light");
  } catch {
    // Private browsing / storage quota — toggle still works for this view.
  }
}
function setDocumentDark(nextDark: boolean): void {
  document.documentElement.classList.toggle("dark", nextDark);
  persistVisitorChoice(nextDark);
}

/**
 * Visitor-facing Light|Dark toggle for the native `theme_switcher` element.
 * See `.cursor/docs/builder/COLOR_MODE_VALUE.md`.
 */
export function VisitorThemeSwitcherControl(t0) {
  const $ = _c(27);
  const {
    interaction,
    variant: t1,
    size,
    iconColor,
    borderColor,
    activeBackground,
    ariaLabel,
    showHiddenPlaceholder: t2,
    disabledReason: t3
  } = t0;
  const variant = t1 === undefined ? "segmented" : t1;
  const showHiddenPlaceholder = t2 === undefined ? false : t2;
  const disabledReason = t3 === undefined ? "global" : t3;
  const rootRef = useRef(null);
  const appearance = variant === "icon" ? "icon" : "segmented";
  let t4;
  let t5;
  if ($[0] !== activeBackground || $[1] !== borderColor || $[2] !== iconColor) {
    t4 = () => {
      const root = rootRef.current;
      if (!root) {
        return;
      }
      applyVtsColorVar(root, "icon", iconColor);
      applyVtsColorVar(root, "border", borderColor);
      applyVtsColorVar(root, "active-bg", activeBackground);
    };
    t5 = [iconColor, borderColor, activeBackground];
    $[0] = activeBackground;
    $[1] = borderColor;
    $[2] = iconColor;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useLayoutEffect(t4, t5);
  let t6;
  if ($[5] !== size) {
    t6 = sizeClass(size);
    $[5] = size;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const rootClass = `${styles.root} ${t6}`;
  if (showHiddenPlaceholder) {
    let t7;
    if ($[7] !== ariaLabel) {
      t7 = ariaLabel?.trim() ? `${ariaLabel.trim()} (hidden)` : "Hidden";
      $[7] = ariaLabel;
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    let t8;
    if ($[9] !== t7) {
      t8 = <span className={styles.hiddenPlaceholder}>{t7}</span>;
      $[9] = t7;
      $[10] = t8;
    } else {
      t8 = $[10];
    }
    let t9;
    if ($[11] !== rootClass || $[12] !== t8) {
      t9 = <div className={rootClass} ref={rootRef} title={"Hidden on the live site \u2014 change Display to Show in the inspector"}>{t8}</div>;
      $[11] = rootClass;
      $[12] = t8;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    return t9;
  }
  if (interaction === "live") {
    let t7;
    if ($[14] !== appearance || $[15] !== ariaLabel) {
      t7 = <LiveVisitorThemeSwitcherControl appearance={appearance} ariaLabel={ariaLabel} />;
      $[14] = appearance;
      $[15] = ariaLabel;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    let t8;
    if ($[17] !== rootClass || $[18] !== t7) {
      t8 = <div className={rootClass} ref={rootRef}>{t7}</div>;
      $[17] = rootClass;
      $[18] = t7;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    return t8;
  }
  const title = interaction === "disabled" ? disabledReason === "instance" ? INSTANCE_DISABLED_HINT : GLOBAL_DISABLED_HINT : PREVIEW_HINT;
  let t7;
  if ($[20] !== appearance || $[21] !== ariaLabel || $[22] !== title) {
    t7 = <StaticVisitorThemeSwitcherChrome appearance={appearance} ariaLabel={ariaLabel} disabled={true} title={title} />;
    $[20] = appearance;
    $[21] = ariaLabel;
    $[22] = title;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== rootClass || $[25] !== t7) {
    t8 = <div className={rootClass} ref={rootRef}>{t7}</div>;
    $[24] = rootClass;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
}
function StaticVisitorThemeSwitcherChrome(t0) {
  const $ = _c(32);
  const {
    appearance,
    ariaLabel,
    disabled,
    title,
    isDark: isDarkProp,
    onSelectLight,
    onSelectDark,
    onToggle
  } = t0;
  const hostRef = useRef(null);
  const [isDark, setIsDark] = useState(isDarkProp ?? false);
  let t1;
  let t2;
  if ($[0] !== isDarkProp) {
    t1 = () => {
      if (isDarkProp != null) {
        setIsDark(isDarkProp);
        return;
      }
      setIsDark(readIsDarkFromDom(hostRef.current));
    };
    t2 = [isDarkProp];
    $[0] = isDarkProp;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== ariaLabel) {
    t3 = ariaLabel?.trim() || "Theme switcher";
    $[3] = ariaLabel;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const label = t3;
  if (appearance === "icon") {
    const t4 = disabled ? undefined : onToggle;
    const t5 = title ?? label;
    let t6;
    if ($[5] !== isDark) {
      t6 = isDark ? <Moon /> : <Sun />;
      $[5] = isDark;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    let t7;
    if ($[7] !== disabled || $[8] !== label || $[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
      t7 = <button aria-label={label} className={styles.iconButton} disabled={disabled} onClick={t4} title={t5} type="button">{t6}</button>;
      $[7] = disabled;
      $[8] = label;
      $[9] = t4;
      $[10] = t5;
      $[11] = t6;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    return t7;
  }
  const t4 = title ?? label;
  const t5 = !isDark;
  const t6 = `${styles.segment}${isDark ? "" : ` ${styles.segmentActive}`}`;
  const t7 = disabled ? undefined : onSelectLight;
  let t8;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Sun />;
    t9 = <span className={styles.segmentLabel}>Light</span>;
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  let t10;
  if ($[15] !== disabled || $[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t10 = <button aria-pressed={t5} className={t6} disabled={disabled} onClick={t7} type="button">{t8}{t9}</button>;
    $[15] = disabled;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = `${styles.segment}${isDark ? ` ${styles.segmentActive}` : ""}`;
  const t12 = disabled ? undefined : onSelectDark;
  let t13;
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Moon />;
    t14 = <span className={styles.segmentLabel}>Dark</span>;
    $[20] = t13;
    $[21] = t14;
  } else {
    t13 = $[20];
    t14 = $[21];
  }
  let t15;
  if ($[22] !== disabled || $[23] !== isDark || $[24] !== t11 || $[25] !== t12) {
    t15 = <button aria-pressed={isDark} className={t11} disabled={disabled} onClick={t12} type="button">{t13}{t14}</button>;
    $[22] = disabled;
    $[23] = isDark;
    $[24] = t11;
    $[25] = t12;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== label || $[28] !== t10 || $[29] !== t15 || $[30] !== t4) {
    t16 = <div aria-label={label} className={styles.segmented} ref={hostRef} role="group" title={t4}>{t10}{t15}</div>;
    $[27] = label;
    $[28] = t10;
    $[29] = t15;
    $[30] = t4;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  return t16;
}
function LiveVisitorThemeSwitcherControl(t0) {
  const $ = _c(15);
  const {
    appearance,
    ariaLabel
  } = t0;
  const [mounted, setMounted] = useState(false);
  const [isDark, setIsDark] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setIsDark(document.documentElement.classList.contains("dark"));
      setMounted(true);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setDocumentDark(false);
      setIsDark(false);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const selectLight = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setDocumentDark(true);
      setIsDark(true);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const selectDark = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const next = !document.documentElement.classList.contains("dark");
      setDocumentDark(next);
      setIsDark(next);
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const toggle = t5;
  if (!mounted) {
    const t6 = appearance === "icon" ? styles.iconButton : styles.segmented;
    let t7;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        visibility: "hidden"
      };
      $[5] = t7;
    } else {
      t7 = $[5];
    }
    let t8;
    if ($[6] !== t6) {
      t8 = <button aria-hidden="true" className={t6} disabled={true} style={t7} tabIndex={-1} type="button" />;
      $[6] = t6;
      $[7] = t8;
    } else {
      t8 = $[7];
    }
    return t8;
  }
  const dynamicLabel = isDark ? "Switch to light theme" : "Switch to dark theme";
  let t6;
  if ($[8] !== ariaLabel || $[9] !== dynamicLabel) {
    t6 = ariaLabel?.trim() || dynamicLabel;
    $[8] = ariaLabel;
    $[9] = dynamicLabel;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const label = t6;
  let t7;
  if ($[11] !== appearance || $[12] !== isDark || $[13] !== label) {
    t7 = <StaticVisitorThemeSwitcherChrome appearance={appearance} ariaLabel={label} isDark={isDark} onSelectDark={selectDark} onSelectLight={selectLight} onToggle={toggle} />;
    $[11] = appearance;
    $[12] = isDark;
    $[13] = label;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}